/**
 * Ячейка одного дня недели в карточке человека (задача 116 §2.2).
 *
 * Четыре фона отделяют состояние заполнения от отклонения часов: сданный день —
 * success, отсутствие/выходной — neutral, просроченный пустой — danger,
 * сегодняшний или будущий пустой — белый. Недобор/переработка остаются только
 * на тексте табеля (§1.2). Календарный выходной получает отдельный маркер даты.
 *
 * `onSelect` (§2.3) — клик по ячейке открывает её день в `TimesheetDayEditor`
 * (правый док, канон задачи 79). Settled-день доступен только при фактическом
 * draft-отчёте; отсутствие/выходной без draft остаются read-only.
 */
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { ru as ruLocale, enUS } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { formatHoursHalfCompact } from "@/lib/timesheet";
import type { WeekDayCell } from "@/api/approval";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { classifyWeekDayPresentation } from "./helpers";

interface WeekDayCellViewProps {
  cell: WeekDayCell;
  /** Уникальный testid ячейки (несёт id человека — дата в ленте не уникальна). */
  testId: string;
  /** Controlled selection из общего `WeekFeedConsole.dock`. */
  selected: boolean;
  /** Клик по ячейке (§2.3) — открыть день в редакторе. Не задан → нередактируема. */
  onSelect?: (date: string) => void;
  /** Единый снимок времени для всех ячеек грида: сегодня ещё не просрочено. */
  now: Date;
}

export function WeekDayCellView({
  cell,
  testId,
  selected,
  onSelect,
  now,
}: WeekDayCellViewProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? enUS : ruLocale;
  const dow = format(parseISO(cell.date), "EEEEEE", { locale });
  const dayNum = format(parseISO(cell.date), "dd", { locale });

  const presentation = classifyWeekDayPresentation(cell, now);
  const submitted =
    cell.state === "undertime" || cell.state === "on_norm" || cell.state === "overtime";
  const deviationHalf = submitted ? cell.declared_hours_half - cell.norm_hours_half : 0;
  const interactive =
    onSelect !== undefined &&
    (cell.status === "draft" || (cell.state !== "weekend" && cell.state !== "absence"));

  const shared = cn(
    "flex min-w-0 flex-1 flex-col gap-1 rounded-[var(--radius-card)] px-2 py-2 text-left",
    interactive &&
      "transition-shadow hover:ring-1 hover:ring-[var(--rule-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-tech)] focus-visible:ring-offset-2",
    interactive && selected && "ring-2 ring-[var(--fg-1)] ring-offset-1",
  );

  const style: React.CSSProperties =
    presentation.surface === "submitted"
      ? {
          border: "1px solid var(--tone-success-border)",
          background: "var(--tone-success-bg)",
        }
      : presentation.surface === "settled"
        ? {
            border: "1px solid var(--tone-neutral-border)",
            background: "var(--bg-panel-1)",
          }
        : presentation.surface === "overdue"
          ? {
              border: "1px solid var(--tone-danger-border)",
              background: "var(--tone-danger-bg)",
            }
          : { border: "1px solid var(--rule-line)", background: "var(--bg-panel-0)" };

  const calendarHeadColor = presentation.showCalendarDayOffMarker
    ? "var(--tone-danger-fg)"
    : undefined;
  const head = (
    <span className="flex items-baseline justify-end gap-1">
      <span
        className="font-condensed text-xs uppercase tracking-[0.06em]"
        style={{ color: calendarHeadColor ?? "var(--fg-3)" }}
      >
        {dow}
      </span>
      <span
        className="font-mono text-sm font-semibold tabular-nums"
        style={{ color: calendarHeadColor ?? "var(--fg-1)" }}
      >
        {dayNum}
      </span>
    </span>
  );
  const body = <CellBody cell={cell} deviationHalf={deviationHalf} testId={testId} />;

  const content = interactive ? (
    <button
      type="button"
      data-testid={testId}
      data-week-day-selectable="true"
      data-state={cell.state}
      aria-pressed={selected}
      onClick={() => onSelect(cell.date)}
      className={shared}
      style={style}
    >
      {head}
      {body}
    </button>
  ) : (
    <div
      data-testid={testId}
      data-state={cell.state}
      className={shared}
      style={style}
      tabIndex={deviationHalf === 0 ? undefined : 0}
    >
      {head}
      {body}
    </div>
  );

  if (deviationHalf === 0) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>
        {deviationHalf < 0
          ? t("week.cell.undertimeTooltip", {
              hours: formatHoursHalfCompact(Math.abs(deviationHalf)),
            })
          : t("week.cell.overtimeTooltip", {
              hours: formatHoursHalfCompact(deviationHalf),
            })}
      </TooltipContent>
    </Tooltip>
  );
}

function CellBody({
  cell,
  deviationHalf,
  testId,
}: {
  cell: WeekDayCell;
  deviationHalf: number;
  testId: string;
}): React.ReactElement {
  const { t } = useTranslation();

  if (cell.state === "weekend") {
    return (
      <span
        className="font-mono font-semibold tabular-nums"
        style={{ color: "var(--fg-1)", fontSize: "var(--fs-16)" }}
      >
        —
      </span>
    );
  }

  if (cell.state === "absence") {
    return (
      <span style={{ color: "var(--fg-3)", fontSize: "var(--fs-13)" }}>
        {cell.absence_event_type?.name ?? t("timeApproval.markBadge")}
      </span>
    );
  }

  if (cell.state === "empty") {
    return (
      <>
        <span
          className="font-mono font-semibold tabular-nums"
          style={{ color: "var(--fg-1)", fontSize: "var(--fs-16)" }}
        >
          —
        </span>
        <span style={{ color: "var(--fg-3)", fontSize: "var(--fs-13)" }}>
          {t("week.cell.noReport")}
        </span>
      </>
    );
  }

  const declaredTone = deviationHalf < 0 ? "caution" : "info";
  return (
    <span
      data-testid={`${testId}-work-row`}
      className="grid w-full grid-cols-1 items-baseline gap-y-0.5 sm:w-fit sm:grid-cols-[auto_auto] sm:gap-x-2 sm:gap-y-1"
    >
      <span style={{ color: "var(--fg-3)", fontSize: "var(--fs-13)" }}>
        {t("week.cell.workLabel")}
      </span>
      <span
        data-testid={`${testId}-work`}
        className="font-mono font-semibold tabular-nums"
        style={{ color: "var(--fg-1)", fontSize: "var(--fs-16)" }}
      >
        {formatHoursHalfCompact(cell.work_hours_half)}
      </span>
      <span style={{ color: "var(--fg-3)", fontSize: "var(--fs-13)" }}>
        {t("week.cell.declaredLabel")}
      </span>
      <span
        data-testid={`${testId}-declared`}
        className={cn(
          "w-fit font-mono font-normal tabular-nums",
          deviationHalf !== 0 && "rounded-[var(--radius-chip)] px-1",
        )}
        style={
          deviationHalf === 0
            ? { color: "var(--fg-3)", fontSize: "var(--fs-13)" }
            : {
                background: `var(--tone-${declaredTone}-bg)`,
                color: `var(--tone-${declaredTone}-fg)`,
                fontSize: "var(--fs-13)",
              }
        }
      >
        {formatHoursHalfCompact(cell.declared_hours_half)}
      </span>
    </span>
  );
}
